import { useId } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { suggestionCreditEstimate } from "./suggestionCreditEstimate";
import { useTranslation } from "react-i18next";

import { CreditIcon } from "@/components/icons/CreditIcon";
import type { AudienceSummary } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { getGoalById } from "@/features/studies/creation/components/goals/goalsConfig";
import { cn } from "@/lib/utils";
import type { TurnSuggestions } from "@digital-twins/shared-types/assistant";
import {
  ArrowRight,
  BarChart3,
  Check,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  FileText,
  ImagePlus,
  ListChecks,
  MessagesSquare,
  MessageSquarePlus,
  X,
} from "@digital-twins/design-system/icons";

interface SuggestionBannerProps {
  suggestion: TurnSuggestions["suggestions"][number];
  index: number;
  audienceName?: string;
  audience?: AudienceSummary;
  disabled: boolean;
  expanded: boolean;
  onToggleDetails: () => void;
  onSelect: () => void;
  onDismiss: () => void;
}

/**
 * The study goal tag and the single muted meta line under the reason (audience,
 * segment and — rooms only — keywords). Pure so the card and its tests can
 * both build it without a DOM.
 */
export function suggestionCardMeta(
  suggestion: TurnSuggestions["suggestions"][number],
  audienceName: string | undefined,
): { studyType: string | null; metaLine: string } {
  if (suggestion.kind === "image") return { studyType: null, metaLine: "" };
  const segment =
    suggestion.kind === "room"
      ? suggestion.spec.twins.segmentLabel
      : suggestion.spec.audience.segmentLabel;
  const parts = [audienceName, segment].filter((value): value is string =>
    Boolean(value?.trim()),
  );
  if (suggestion.kind === "room") {
    const keywords = (suggestion.spec.twins.keywords ?? []).filter(
      (keyword): keyword is string => Boolean(keyword?.trim()),
    );
    parts.push(...keywords);
  }

  let studyType: string | null = null;
  if (suggestion.kind === "study") {
    const goal = suggestion.spec.goalId
      ? getGoalById(suggestion.spec.goalId)
      : null;
    studyType = goal?.methodTag?.trim() || null;
  }

  return { studyType, metaLine: parts.join(" · ") };
}

export function SuggestionBanner(props: SuggestionBannerProps) {
  const {
    suggestion,
    index,
    audienceName,
    audience,
    disabled,
    onSelect,
    onDismiss,
    expanded,
    onToggleDetails,
  } = props;
  const { t } = useTranslation("app");
  const reducedMotion = useReducedMotion();
  const detailsId = useId();
  const { studyType, metaLine } = suggestionCardMeta(suggestion, audienceName);

  const isGoalTag = suggestion.kind === "study" && Boolean(studyType);
  const isRoom = suggestion.kind === "room";
  const items =
    suggestion.kind === "study"
      ? suggestion.spec.keyQuestions
      : suggestion.kind === "room"
        ? [suggestion.spec.topic, suggestion.spec.openingQuestion]
        : [suggestion.spec.ask];
  const estimate = suggestionCreditEstimate(suggestion, audience);
  const {
    kindLabel,
    Icon,
    chipClassName,
    prepareLabel,
    detailsLabel,
    deliverables,
  } = {
    study: {
      kindLabel: t("threads.detail.sourcePill.kind.study"),
      Icon: ClipboardList,
      chipClassName: "bg-primary text-primary-foreground",
      prepareLabel: t("threads.sessions.suggestionCard.prepareStudy"),
      detailsLabel: t("threads.sessions.questionsToExplore"),
      deliverables: [
        {
          Icon: ListChecks,
          label: t("threads.sessions.suggestionCard.questionnaire"),
        },
        {
          Icon: BarChart3,
          label: t("threads.sessions.suggestionCard.analysis"),
        },
        { Icon: FileText, label: t("threads.sessions.suggestionCard.report") },
      ],
    },
    quick_study: {
      kindLabel: t("threads.quickStudy.title"),
      Icon: MessageSquarePlus,
      chipClassName: "bg-mint text-mint-foreground",
      prepareLabel: t("threads.sessions.suggestionCard.prepareQuickStudy"),
      detailsLabel: t("threads.sessions.questionsToExplore"),
      deliverables: [
        {
          Icon: ListChecks,
          label: t("threads.sessions.suggestionCard.twoQuestions"),
        },
        {
          Icon: Check,
          label: t("threads.sessions.suggestionCard.threadResults"),
        },
        { Icon: BarChart3, label: t("threads.sessions.suggestionCard.plots") },
      ],
    },
    room: {
      kindLabel: t("threads.detail.sourcePill.kind.room"),
      Icon: MessagesSquare,
      chipClassName: "bg-primary-dark text-primary-dark-foreground",
      prepareLabel: t("threads.sessions.suggestionCard.startConversation"),
      detailsLabel: t("threads.sessions.topicsToDiscuss"),
      deliverables: [
        {
          Icon: MessagesSquare,
          label: t("threads.sessions.suggestionCard.discussion"),
        },
      ],
    },
    image: {
      kindLabel: t("threads.sessions.suggestionCard.image"),
      Icon: ImagePlus,
      chipClassName:
        "bg-primary/10 text-foreground ring-1 ring-inset ring-primary/25",
      prepareLabel: t("threads.sessions.suggestionCard.prepareImage"),
      detailsLabel: t("threads.sessions.suggestionCard.imageBrief"),
      deliverables: [
        {
          Icon: ImagePlus,
          label: t("threads.sessions.suggestionCard.imagePreview"),
        },
      ],
    },
  }[suggestion.kind];

  return (
    <article
      className="group relative row-span-5 grid w-full snap-start grid-rows-subgrid gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm"
      data-testid={`thread-suggestion-card-${index}`}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2 pr-7">
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1",
            chipClassName,
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
          <Text as="span" size="xs" weight="medium" className="text-inherit">
            {kindLabel}
          </Text>
        </span>
        {studyType ? (
          <Text
            as="span"
            size="xs"
            color="muted"
            data-testid={
              isGoalTag ? `thread-suggestion-goal-${index}` : undefined
            }
          >
            {studyType}
          </Text>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <Text
          as="span"
          size="base"
          weight="semibold"
          className="break-words leading-snug"
        >
          {suggestion.title}
        </Text>
        <Text
          as="span"
          size="sm"
          color="muted"
          className={cn(
            "break-words leading-relaxed",
            !expanded && "line-clamp-2",
          )}
        >
          {suggestion.why}
        </Text>
        <AnimatePresence initial={false}>
          {expanded ? (
            <motion.div
              id={detailsId}
              initial={reducedMotion ? false : { height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                duration: reducedMotion ? 0 : 0.16,
                ease: "easeOut",
              }}
              className="overflow-hidden"
            >
              <div className="flex flex-col gap-3 border-t border-border pt-3">
                <Text as="span" size="xs" color="muted">
                  {detailsLabel}
                </Text>
                <div
                  role="list"
                  className="flex flex-col gap-2"
                  data-testid={`thread-suggestion-outline-${index}`}
                >
                  {items.map((item, itemIndex) => (
                    <div
                      role="listitem"
                      key={itemIndex}
                      className="flex items-start gap-2 text-sm leading-relaxed"
                    >
                      <span
                        className="mt-2 size-1 shrink-0 rounded-full bg-primary"
                        aria-hidden
                      />
                      <Text as="span" size="sm" className="min-w-0 break-words">
                        {item}
                      </Text>
                    </div>
                  ))}
                </div>
                {metaLine ? (
                  <Text
                    as="span"
                    size="xs"
                    color="muted"
                    className="break-words"
                  >
                    {metaLine}
                  </Text>
                ) : null}
                {suggestion.kind === "study" ||
                suggestion.kind === "quick_study" ? (
                  <Text as="span" size="xs" color="muted">
                    {t(
                      "threads.sessions.suggestionCard.finalPriceAfterPreparation",
                    )}
                  </Text>
                ) : null}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
      <footer className="contents">
        <div
          className="flex flex-wrap gap-x-3 gap-y-1.5"
          data-testid={`thread-suggestion-deliverables-${index}`}
        >
          {deliverables.map(({ Icon: DeliverableIcon, label }) => (
            <Text
              key={label}
              as="span"
              size="xs"
              color="muted"
              className="inline-flex items-center gap-1.5"
            >
              <DeliverableIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {label}
            </Text>
          ))}
        </div>
        <Text
          as="span"
          size="xs"
          color="muted"
          className="inline-flex items-center gap-1.5 border-t border-border pt-3"
          data-testid={`thread-suggestion-credits-${index}`}
        >
          <CreditIcon className="size-3.5 shrink-0" />
          {estimate.credits !== null
            ? t(
                `threads.sessions.suggestionCard.${isRoom ? "lowMessagePrice" : "startingPrice"}`,
                { count: estimate.credits },
              )
            : t("threads.sessions.suggestionCard.priceBeforeApproval")}
          {estimate.sampleSize !== null ? (
            <span>
              ·{" "}
              {t("threads.sessions.suggestionCard.sampleSize", {
                count: estimate.sampleSize,
              })}
            </span>
          ) : null}
        </Text>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="px-0 text-muted-foreground"
            aria-expanded={expanded}
            aria-controls={expanded ? detailsId : undefined}
            onClick={onToggleDetails}
          >
            {t(
              `threads.sessions.suggestionCard.${expanded ? "showLess" : "showDetails"}`,
            )}
            {expanded ? (
              <ChevronUp className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            data-testid={`thread-suggestion-${index}`}
            aria-label={`${prepareLabel}: ${suggestion.title}`}
            onClick={onSelect}
          >
            {prepareLabel}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </footer>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-8 w-8 text-muted-foreground hover:text-foreground"
        aria-label={t("threads.sessions.dismiss")}
        data-testid={`thread-suggestion-dismiss-${index}`}
        onClick={onDismiss}
      >
        <X className="h-4 w-4" aria-hidden />
      </Button>
    </article>
  );
}
